import { Popover, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { useActionRunner } from 'src/modules/notification'
import { Typography } from 'src/modules/ui'
import { formatDateTime } from 'src/utils'

import { useApp } from '../hooks'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
  },
  notification: {
    borderRadius: theme.shape.borderRadius,
    display: 'block',
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(1),
    textDecoration: 'inherit',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  notificationUnread: {
    backgroundColor: theme.palette.fadedPurple.main,
  },
  noNotifications: {
    padding: theme.spacing(1),
    textAlign: 'center',
    width: 200,
  },
}))

const notificationText = ({ byOrganisation, params, type }) => {
  const makeNotificationText = {
    order_cancelled: () => `Order cancelled by ${byOrganisation.name}`,
    order_dispatched: () => `Order dispatched by ${byOrganisation.name}`,
    order_approved: () => `Order approved by ${byOrganisation.name}`,
    order_updated: () => `Order updated by ${byOrganisation.name}`,
    order_issue_raised: () => `Order issue raised by ${byOrganisation.name}`,
    order_placed: () => `New order from ${byOrganisation.name}`,
    agreement_accepted: () => `Proposal accepted by ${byOrganisation.name}`,
    agreement_proposed: () => `New proposal from ${byOrganisation.name}`,
    agreement_updated: () => `Agreement updated by ${byOrganisation.name}`,
    proposal_requested: () => `Proposal requested by ${byOrganisation.name}`,
  }

  const callable = makeNotificationText[type] ?? null

  if (!callable) {
    console.warn('No text for notification type', type)
    return null
  }

  return callable()
}

const notificationLink = ({ byOrganisation, type, params }) => {
  const makeLink = {
    order_cancelled: () => `/purchasing/submitted/${params.orderId}`,
    order_dispatched: () => `/purchasing/submitted/${params.orderId}`,
    order_approved: () => `/purchasing/submitted/${params.orderId}`,
    order_updated: () => `/purchasing/submitted/${params.orderId}`,
    order_issue_raised: () => `/purchasing/submitted/${params.orderId}`,
    order_placed: () => `/orders/${params.orderId}`,
    agreement_accepted: () => `/customers/${params.agreementId}`,
    agreement_proposed: () => `/suppliers/${byOrganisation.id}/agreement`,
    agreement_updated: () => `/suppliers/${byOrganisation.id}/agreement`,
    proposal_requested: () => `/customers/${params.agreementId}`,
  }

  const callable = makeLink[type] ?? null

  if (!callable) {
    console.warn('No link for notification type', type)
    return null
  }

  return callable()
}

export const Notifications = ({
  isFetching,
  notifications,
  onClose,
  onOpen,
  trigger,
}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = event => {
    onOpen()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    onClose()
    setAnchorEl(null)
  }

  return (
    <>
      {trigger({ onClick: handleClick })}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className={classes.root}>
          {isFetching ? (
            <Typography
              className={classes.noNotifications}
              color="textSecondary"
            >
              Loading...
            </Typography>
          ) : notifications.length === 0 ? (
            <Typography
              className={classes.noNotifications}
              color="textSecondary"
            >
              No notifications yet!
            </Typography>
          ) : (
            notifications.map(notification => (
              <Link
                className={clsx(classes.notification, {
                  [classes.notificationUnread]: !notification.read,
                })}
                onClick={handleClose}
                to={notificationLink(notification)}
              >
                <Typography color="orange">
                  {notificationText(notification)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatDateTime(notification.createdAt)}
                </Typography>
              </Link>
            ))
          )}
        </div>
      </Popover>
    </>
  )
}

Notifications.propTypes = {
  isFetching: PropTypes.bool,
  notifications: PropTypes.array,
  onOpen: PropTypes.func,
  trigger: PropTypes.func,
}

Notifications.defaultProps = {
  notifications: [],
  onClose: () => {},
  onOpen: () => {},
}

const NotificationsContainer = ({ trigger }) => {
  const { fetchNotifications, markNotificationsRead, notifications } = useApp()
  const [doFetchNotifications, isFetching] = useActionRunner(fetchNotifications)

  const handleOpen = () => {
    doFetchNotifications()
  }

  const handleClose = () => {
    markNotificationsRead()
  }

  return (
    <Notifications
      isFetching={isFetching && notifications.length === 0}
      notifications={notifications}
      onClose={handleClose}
      onOpen={handleOpen}
      trigger={trigger}
    />
  )
}

export default NotificationsContainer
